import { UserModel } from 'models';
import ThrottledApi from './api/throttledApi';
import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { USER_ENDPOINTS } from 'constants/apiEndPoints';

// ===== GET OPERATIONS =====

// Get current user details (returns UserModel)
export const getCurrentUser = async () => {
  console.log('👥 UserService: Getting current user');
  try {
    const response = await ThrottledApi.get(USER_ENDPOINTS.GET_CURRENT_USER) as AxiosResponse;
    console.log('👥 UserService: Response:', response);
    console.log('👥 UserService: Response.data:', response.data);
    console.log('👥 UserService: Response.data type:', typeof response.data);
    console.log('👥 UserService: Response.data keys:', Object.keys(response.data || {}));
    
    if (response.data) {
      console.log('👥 UserService: Creating UserModel with data:', response.data);
      const userModel = UserModel.fromServerData(response.data);
      console.log('👥 UserService: Created UserModel:', userModel);
      console.log('👥 UserService: UserModel name:', userModel.name);
      return userModel;
    }
    console.log('👥 UserService: No data, returning empty UserModel');
    const emptyModel = UserModel.fromServerData({});
    console.log('👥 UserService: Empty UserModel:', emptyModel);
    return emptyModel;
  } catch (error: any) {
    console.error('👥 UserService: Error getting current user:', error);
    const emptyModel = UserModel.fromServerData({});
    return emptyModel;
  }
};

// Get users with pagination and optional store filter (returns array of UserModel)
export const getUsers = async (skip = 0, limit = 10, storeId: string | null | undefined = null) => {
  const url = USER_ENDPOINTS.LIST_USERS(skip, limit, storeId ?? undefined);
  try {
    const response = await ThrottledApi.get(url) as AxiosResponse;
    if (Array.isArray(response.data)) {
      return response.data.map((user: any) => UserModel.fromServerData(user));
    }
    return [];
  } catch (error: any) {
    console.error('👥 UserService: Error getting users:', error);
    return [];
  }
};

// ===== POST/UPDATE OPERATIONS (ACTUALLY USED) =====

export const createUser = async (data: any) => {
  try {
    const response = await ThrottledApi.post(USER_ENDPOINTS.CREATE_USER, data) as AxiosResponse;
    return response.data;
  } catch (error: any) {
    console.error('👥 UserService: Error creating user:', error);
    throw error;
  }
};

export const updateUser = async (id: string | number, data: any) => {
  try {
    const response = await ThrottledApi.put(USER_ENDPOINTS.UPDATE_USER(id), data) as AxiosResponse;
    return response.data;
  } catch (error: any) {
    console.error('👥 UserService: Error updating user:', error);
    throw error;
  }
};

// ===== STORE-SPECIFIC USER OPERATIONS =====
export const listStoreUsers = async (storeId: string, options?: { suppressErrorToast?: boolean }): Promise<{ success: boolean; data?: any; error?: string }> => {
  console.log('🏪 StoreService: Listing store users for store', storeId);
  try {
      const config = { suppressErrorToast: options?.suppressErrorToast } as AxiosRequestConfig;
      const response = await ThrottledApi.get(USER_ENDPOINTS.LIST_STORE_USERS(storeId), config) as AxiosResponse;

      if (response?.data) {
          return {
              success: true,
              data: response.data,
          };
      }

      return {
          success: false,
          error: response?.data?.error || 'Failed to list store users',
      };
  } catch (error: any) {
      console.error('StoreService: User listing error:', error);
      throw error;
  }
}

export const inviteUserToStore = async (storeId: string, userData: any): Promise<{ success: boolean; data?: any; error?: string }> => {
  try {
      const response = await ThrottledApi.post(USER_ENDPOINTS.INVITE_USER_TO_STORE(storeId), userData) as AxiosResponse;

      if (response?.data) {
          return {
              success: true,
              data: response.data.data,
          };
      }

      return {
          success: false,
          error: response?.data?.error || 'Failed to add user',
      };
  } catch (error: any) {
      console.error('StoreService: User invitation error:', error);
      throw error;
  }
};

export const editUserInStore = async (storeId: string, userId: string, userData: any): Promise<{ success: boolean; data?: any; error?: string }> => {
  try {
      const response = await ThrottledApi.put(USER_ENDPOINTS.EDIT_USER_IN_STORE(storeId, userId), userData) as AxiosResponse;

      if (response?.data) {
          return {
              success: true,
              data: response.data.data,
          };
      }

      return {
          success: false,
          error: response?.data?.error || 'Failed to update user',
      };
  } catch (error: any) {
      console.error('StoreService: User update error:', error);
      throw error;
  }
};

export const setCashierModePin = async (storeId: string, userId: string, pin: string | null): Promise<{ success: boolean; data?: any; error?: string }> => {
  try {
      const response = await ThrottledApi.put(USER_ENDPOINTS.SET_CASHIER_MODE_PIN(storeId, userId), { pin }) as AxiosResponse;

      if (response?.data) {
          return {
              success: true,
              data: response.data,
          };
      }

      return {
          success: false,
          error: response?.data?.error || 'Failed to update cashier-mode PIN',
      };
  } catch (error: any) {
      console.error('StoreService: Cashier-mode PIN update error:', error?.response?.status || error?.message);
      throw error;
  }
};

// Alias for backward compatibility
export const updateStoreUser = async (storeId: string, userId: string, userData: any): Promise<{ success: boolean; data?: any; error?: string }> => {
  return editUserInStore(storeId, userId, userData);
};

export const deleteUserFromStore = async (storeId: string, userId: string): Promise<{ success: boolean; error?: string }> => {
  try {
      const response = await ThrottledApi.delete(USER_ENDPOINTS.DELETE_STORE_USER(storeId, userId)) as AxiosResponse;

      if (response?.data || response?.status === 204) {
          return { success: true };
      }

      return {
          success: false,
          error: response?.data?.error || 'Failed to delete user',
      };
  } catch (error: any) {
      console.log('StoreService: User deletion error:', error);
      throw error;
  }
};

// Delete user - used in DeleteUser component
export const deleteUser = async (userId: string) => {
  console.log('👥 UserService: Deleting user:', userId);
  try {
    await ThrottledApi.delete(USER_ENDPOINTS.DELETE_USER(userId));
    
    console.log('👥 UserService: User deleted successfully');
    return {
      success: true,
      data: null,
      error: null
    };
  } catch (error: any) {
    console.log('👥 UserService: User deletion failed:', error);
    return {
      success: false,
      data: null,
      error: error.response?.data || error.message
    };
  }
};

// ===== BACKWARD COMPATIBILITY ALIASES =====

// Alias for getCurrentUser
export const getUserDetails = async () => {
  return getCurrentUser();
};

// Alias for getUsers
export const getStoreUsers = async (storeId: string | null | undefined = null) => {
  return getUsers(0, 1000, storeId);
};

// Export all user functions as a single object for convenience
export const userService = {
  getCurrentUser,
  getUsers,
  deleteUser,
  getUserDetails,
  getStoreUsers,
  listStoreUsers,
  inviteUserToStore,
  editUserInStore,
  setCashierModePin,
  updateStoreUser,
  deleteUserFromStore
};

export default userService; 
